import React, { RefObject, useEffect, useRef } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { Dimensions, Text, View } from 'react-native';

import { AutocompleteAddressInput } from 'components/autocomplete-address-input';
import type { PlacesSessionToken } from 'hooks/geolocation/types';
import { LogUserErrorSeverity, logUserErrorMessage } from 'hooks/log-user-error-message';
import { t } from 'state/intl/translations';
import { FormValidationState } from 'utils/form';
import { IPlaceAddress, IPlaceData } from 'utils/geolocation';

import { IPhoneAddressValidation } from '../types';

import { lockInputStyle } from './styled';

interface IDeliveryAddressFormInputProps {
  addressString: string;
  deliveryAddressInputRef?: React.RefObject<HTMLInputElement | null>;
  message: string;
  setAddressComponents: (address: IPlaceAddress | null) => void;
  setAddressString: (address: string) => void;
  setCoordinates: (coordinates: undefined) => void;
  setMessage: (message: string) => void;
  setPlaceId: (placeId: string) => void;
  setValidation: (validation: IPhoneAddressValidation) => void;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  unitRef: RefObject<HTMLInputElement | null>;
  validation: IPhoneAddressValidation;
  onHandleSetAddress: (
    placeId: string,
    placeData?: IPlaceData,
    sessionToken?: PlacesSessionToken
  ) => void | Promise<void>;
  isAddressLocked?: boolean;
}

export const DeliveryAddressFormInput: React.FC<React.PropsWithChildren<
  IDeliveryAddressFormInputProps
>> = ({
  setAddressString,
  setPlaceId,
  setValidation,
  setMessage,
  unitRef,
  validation,
  setCoordinates,
  setAddressComponents,
  addressString,
  deliveryAddressInputRef,
  message,
  setValue,
  onHandleSetAddress,
  isAddressLocked,
}) => {
  const defaultAddressInputRef = useRef<HTMLInputElement>(null);

  const AddressInputRef = deliveryAddressInputRef ?? defaultAddressInputRef;

  const handleAddressChange = async (
    newAddressString: string,
    placeId?: string,
    placeData?: IPlaceData,
    sessionToken?: PlacesSessionToken
  ) => {
    if (isAddressLocked) {
      return;
    }
    setAddressString(newAddressString);
    setMessage('');

    if (!placeId) {
      setValidation({
        ...validation,
        address: FormValidationState.INVALID,
      });
      const addressError = t('addressError');
      setMessage(addressError);
      logUserErrorMessage({
        message: addressError,
        severity: LogUserErrorSeverity.Recoverable,
        context: { moreInfo: 'no-place-id' },
      });
      setAddressComponents(null);
      return;
    }

    if (unitRef.current) {
      unitRef.current.focus();
    }

    setValidation({
      ...validation,
      address: FormValidationState.PRISTINE,
    });
    setPlaceId(placeId);
    setCoordinates(undefined);

    await onHandleSetAddress(placeId, placeData, sessionToken);
  };

  useEffect(() => {
    if (!isAddressLocked && !addressString && AddressInputRef.current) {
      AddressInputRef.current.focus();
    }
  }, [isAddressLocked, addressString, AddressInputRef]);

  const screenWidth = Dimensions.get('window').width;
  return isAddressLocked ? (
    <View style={lockInputStyle.lockedContainer}>
      <Text
        style={[lockInputStyle.lockedText, { fontSize: screenWidth < 420 ? 13 : 14 }]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {addressString || t('deliveryAddressPlaceholder', 'Enter delivery address')}
      </Text>
      <View style={lockInputStyle.iconContainer}>
        <Icon testID="locked" variant="locked" size="md" color="gray.400" />
      </View>
    </View>
  ) : (
    <AutocompleteAddressInput
      autoFocus
      data-private="redact"
      testID="delivery-address-input"
      label={t('deliveryAddress')}
      addressesOnly
      message={message}
      onAddressSelected={handleAddressChange}
      onInputValueChange={val => {
        if (isAddressLocked) {
          return;
        }
        setValue((val ?? '') as string);
      }}
      ref={AddressInputRef}
      required
      setMessage={setMessage}
      useDeliveryPredictions={true}
      value={addressString}
      inputCustomProps={{
        py: '$4',
        minHeight: 60,
        fontSize: 'md',
        isDisabled: isAddressLocked,
        editable: !isAddressLocked,
        selectTextOnFocus: false,
        pointerEvents: isAddressLocked ? 'none' : 'auto',
      }}
      rightIconCustomProps={
        isAddressLocked ? { variant: 'Lock', size: 'md', color: 'gray.400' } : undefined
      }
    />
  );
};
